import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";
import { EducationPgmProgram, Person, PgmProgramRequirement, PgmRequirementStatus, ProgramTypeName } from "@/types";
import {
  QUALIFYING_LEVEL_REQUIREMENTS_SCROLL_ID,
  membershipProgressCardNavigation,
  type MembershipProgressLinkOptions
} from "./membershipProgressLinks";
import { buildQualifyingLevelMetric } from "./qualifyingLevelMetric";
import {
  buildLevelMetric,
  hasStartedPgmLevelOne,
  isOverviewPgmProgressSupported,
  isQualifyingLevelComplete,
  mapHistoryToLevel,
  mapProgressToLevel,
  PGM_LEVEL_STATUS,
  type LevelMetricInput
} from "./pgm-levels";

function formatLevelName(level: number, careerPath?: string | null): string {
  if (level === 0) return "Qualifying Level";
  return careerPath ? `Level ${level} ${careerPath}` : `Level ${level}`;
}

type CurrentPgmLevelContext = {
  level: LevelMetricInput;
  progressRequirements?: PgmRequirementStatus[];
  historyLevel?: EducationPgmProgram;
};

function countProgressRequirements(requirements: PgmRequirementStatus[]): { completed: number; total: number } | null {
  if (requirements.length === 0) return null;

  return {
    completed: requirements.filter((requirement) => requirement.status === PGM_LEVEL_STATUS.COMPLETE).length,
    total: requirements.length
  };
}

function countHistoryRequirements(
  requirements: PgmProgramRequirement[],
  levelCode: string
): { completed: number; total: number } | null {
  const levelRequirements = requirements.filter((requirement) => requirement.levelCode === levelCode);
  if (levelRequirements.length === 0) return null;

  return {
    completed: levelRequirements.filter((requirement) => requirement.isCompleted).length,
    total: levelRequirements.length
  };
}

function getCurrentLevelRequirementCounts(
  context: CurrentPgmLevelContext
): { completed: number; total: number } | null {
  if (context.progressRequirements) {
    return countProgressRequirements(context.progressRequirements);
  }

  if (context.historyLevel) {
    return countHistoryRequirements(context.historyLevel.programRequirements ?? [], context.historyLevel.levelCode);
  }

  return null;
}

function mergeRequirementTooltip(
  existingTooltip: MetricCardType<Path>["tooltip"],
  requirementCounts: { completed: number; total: number }
): MetricCardType<Path>["tooltip"] {
  const requirementsText = `${requirementCounts.completed} of ${requirementCounts.total} requirements completed`;

  if (!existingTooltip) {
    return { supportingText: requirementsText };
  }

  return {
    ...existingTooltip,
    supportingText: existingTooltip.supportingText
      ? `${requirementsText}\n${existingTooltip.supportingText}`
      : requirementsText
  };
}

function getPgmOverviewProgramType(
  person: Person
): ProgramTypeName.Associate | ProgramTypeName.Affiliate | ProgramTypeName.Student | null {
  const programType = person.latestProgram?.programType?.name;
  if (
    programType === ProgramTypeName.Associate ||
    programType === ProgramTypeName.Affiliate ||
    programType === ProgramTypeName.Student
  ) {
    return programType;
  }
  return null;
}

/**
 * Membership program type does not always match PGM curriculum program_type.
 * Students may be on Student (S/U/V) or Associate (F/A/N) tracks; Affiliates use Associate codes.
 */
function compatiblePgmProgramTypes(
  membershipProgramType: ProgramTypeName.Associate | ProgramTypeName.Affiliate | ProgramTypeName.Student
): string[] {
  switch (membershipProgramType) {
    case ProgramTypeName.Student:
      return [ProgramTypeName.Student, ProgramTypeName.Associate];
    case ProgramTypeName.Affiliate:
      return [ProgramTypeName.Associate, ProgramTypeName.Affiliate];
    default:
      return [ProgramTypeName.Associate];
  }
}

function isCompatiblePgmProgramType(
  membershipProgramType: ProgramTypeName.Associate | ProgramTypeName.Affiliate | ProgramTypeName.Student,
  pgmProgramType?: string | null
): boolean {
  if (!pgmProgramType) return false;
  return compatiblePgmProgramTypes(membershipProgramType).includes(pgmProgramType);
}

function getHistoryPgmLevelContext(
  person: Person,
  membershipProgramType: ProgramTypeName.Associate | ProgramTypeName.Affiliate | ProgramTypeName.Student
): CurrentPgmLevelContext | null {
  const { educationLatestPgmProgram, educationPgmProgramHistory = [] } = person;

  if (
    educationLatestPgmProgram &&
    isCompatiblePgmProgramType(membershipProgramType, educationLatestPgmProgram.programType)
  ) {
    const compatibleHistory = educationPgmProgramHistory.filter((item) =>
      isCompatiblePgmProgramType(membershipProgramType, item.programType)
    );
    const historyMatch =
      compatibleHistory.find(
        (item) =>
          item.level === educationLatestPgmProgram.level && item.levelCode === educationLatestPgmProgram.levelCode
      ) ?? compatibleHistory.find((item) => item.level === educationLatestPgmProgram.level);

    return {
      level: {
        name: formatLevelName(educationLatestPgmProgram.level, educationLatestPgmProgram.careerPath),
        curriculumVersion: educationLatestPgmProgram.curriculumVersion,
        startDate: educationLatestPgmProgram.startDate,
        completionDate: educationLatestPgmProgram.endDate,
        expirationDate: historyMatch?.levelExpirationDate ?? null,
        isCompleted: educationLatestPgmProgram.completed
      },
      historyLevel: historyMatch
    };
  }

  const programHistory = educationPgmProgramHistory.filter((item) =>
    isCompatiblePgmProgramType(membershipProgramType, item.programType)
  );
  const activeLevel = [...programHistory].filter((item) => !item.completed).sort((a, b) => b.level - a.level)[0];

  if (activeLevel) {
    const level = mapHistoryToLevel(
      formatLevelName(activeLevel.level, activeLevel.careerPath),
      activeLevel.level,
      programHistory
    );
    if (!level) return null;

    return { level, historyLevel: activeLevel };
  }

  const latestCompletedLevel = [...programHistory]
    .filter((item) => item.completed)
    .sort((a, b) => b.level - a.level)[0];

  if (!latestCompletedLevel) return null;

  const level = mapHistoryToLevel(
    formatLevelName(latestCompletedLevel.level, latestCompletedLevel.careerPath),
    latestCompletedLevel.level,
    programHistory
  );
  if (!level) return null;

  return { level, historyLevel: latestCompletedLevel };
}

function getCurrentPgmLevelContext(person: Person): CurrentPgmLevelContext | null {
  const membershipProgramType = getPgmOverviewProgramType(person);
  if (!membershipProgramType) return null;

  const { pgmProgressStatus } = person;

  if (
    pgmProgressStatus &&
    isCompatiblePgmProgramType(membershipProgramType, pgmProgressStatus.programType) &&
    isOverviewPgmProgressSupported(pgmProgressStatus.curriculumVersion, pgmProgressStatus.programType)
  ) {
    const { currentLevelKey, levels, curriculumVersion } = pgmProgressStatus;
    const currentLevel =
      levels.find((level) => level.key === currentLevelKey) ??
      levels.find((level) => level.status === PGM_LEVEL_STATUS.IN_PROGRESS);

    if (currentLevel && currentLevel.status !== PGM_LEVEL_STATUS.LOCKED) {
      const level = mapProgressToLevel(currentLevel, curriculumVersion);
      if (level) {
        return {
          level,
          progressRequirements: currentLevel.requirements ?? []
        };
      }
    }

    const latestMeaningful = [...levels]
      .filter((level) => level.status !== PGM_LEVEL_STATUS.LOCKED && (level.startDate || level.completionDate))
      .sort((a, b) => b.numeral - a.numeral)[0];

    if (latestMeaningful) {
      const level = mapProgressToLevel(latestMeaningful, curriculumVersion);
      if (level) {
        return {
          level,
          progressRequirements: latestMeaningful.requirements ?? []
        };
      }
    }
  }

  return getHistoryPgmLevelContext(person, membershipProgramType);
}

function pgmMetricLink(
  person: Person,
  options: MembershipProgressLinkOptions
): Pick<MetricCardType<Path>, "link" | "onClick"> {
  if (!person.pgaId) return {};

  if (shouldLinkPgmMetricToProspectProgram(person)) {
    return membershipProgressCardNavigation(person.pgaId, {
      ...options,
      scrollTo: QUALIFYING_LEVEL_REQUIREMENTS_SCROLL_ID
    });
  }

  return {
    link: {
      to: "/people/:pgaId/membership/path-to-membership",
      params: { pgaId: person.pgaId }
    }
  };
}

/** Affiliates who finished QL but have not started Level 1 should open prospect-path details. */
function shouldLinkPgmMetricToProspectProgram(person: Person): boolean {
  return (
    person.latestProgram?.programType?.name === ProgramTypeName.Affiliate &&
    isQualifyingLevelComplete(person) &&
    !hasStartedPgmLevelOne(person)
  );
}

function withOverviewPgmChrome(
  metric: MetricCardType<Path>,
  person: Person,
  options: MembershipProgressLinkOptions
): MetricCardType<Path> {
  return {
    ...metric,
    iconName: metric.iconName === "lock" ? "school" : metric.iconName,
    value: metric.iconName === "lock" ? "PGM" : `PGM ${metric.value}`,
    ...pgmMetricLink(person, options)
  };
}

export function buildAssociateCurrentPgmMetric(
  person: Person,
  isSuspended: boolean,
  options: MembershipProgressLinkOptions = {}
): MetricCardType<Path> | null {
  if (!getPgmOverviewProgramType(person)) {
    return null;
  }

  // Path to Membership owns Qualifying Level completeness (leftover Purchase Level 1,
  // Affiliate 3.1 progress still in_progress). Reuse that card until Level 1 starts.
  if (!hasStartedPgmLevelOne(person)) {
    return withOverviewPgmChrome(buildQualifyingLevelMetric(person, isSuspended), person, options);
  }

  const context = getCurrentPgmLevelContext(person);
  if (!context) {
    return {
      label: "Not Started",
      value: "PGM",
      iconName: "school",
      ...pgmMetricLink(person, options)
    };
  }

  const { level } = context;
  const metric = buildLevelMetric(level, level.name, isSuspended);
  const requirementCounts = getCurrentLevelRequirementCounts(context);
  const tooltip = requirementCounts ? mergeRequirementTooltip(metric.tooltip, requirementCounts) : metric.tooltip;

  return withOverviewPgmChrome({ ...metric, tooltip }, person, options);
}
